const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const finitePositive = (value) => {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : 0;
};

export const median = (values) => {
  const sorted = values.filter(Number.isFinite).slice().sort((a, b) => a - b);
  if (!sorted.length) return 0;
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2
    ? sorted[middle]
    : (sorted[middle - 1] + sorted[middle]) / 2;
};

const medianAbsoluteDeviation = (values, center = median(values)) =>
  median(values.map((value) => Math.abs(value - center)));

const robustFilter = (rows, priceSelector, outlierFloorBps) => {
  if (rows.length < 3) return rows;
  const prices = rows.map(priceSelector);
  const center = median(prices);
  const mad = medianAbsoluteDeviation(prices, center);
  const floor = center * (Math.max(1, outlierFloorBps) / 10000);
  const allowedDeviation = Math.max(floor, mad * 4);
  const filtered = rows.filter((row) => Math.abs(priceSelector(row) - center) <= allowedDeviation);
  return filtered.length >= 2 ? filtered : rows;
};

const DEFAULT_CONFIG = Object.freeze({
  minSources: 3,
  staleAfterMs: 3500,
  historyWindowMs: 60000,
  momentumWindowMs: 5000,
  outlierFloorBps: 12,
  maxDispersionBps: 25,
  minSignalQuality: 0.55,
  minSizeMultiplier: 0.9,
  maxSizeMultiplier: 1.1,
  opportunityScaleBps: 18,
  momentumScaleBps: 12,
  minDirectionalQuality: 0.65,
  minDirectionalLeadBps: 2.5,
  directionalLeadScaleBps: 10,
  minDirectionalMomentumBps: 1,
  minDirectionalAgreement: 0.66,
  minMomentumConsistency: 0.5,
  dispersionNoiseMultiplier: 2,
  executionToleranceBps: 3,
  maxUpsizeWithoutNetOpportunity: 1.05,
  maxThresholdAdjustmentPct: 0.05,
  estimatedExitCostBps: 8,
  safetyMarginBps: 5,
  fallbackGasUsd: 0.05,
});

export const DEFAULT_EXTERNAL_SIGNAL_CONFIG = DEFAULT_CONFIG;

const safeTimestamp = (value, fallback) => {
  if (typeof value === "string") {
    const parsed = Date.parse(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric <= 0) return fallback;
  return numeric < 1e12 ? numeric * 1000 : numeric;
};

class ExternalMarketSignalService {
  constructor(config = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.feeds = new Map();
    this.sockets = new Map();
    this.reconnectTimers = new Map();
    this.pingTimers = new Map();
    this.reconnectAttempts = new Map();
    this.running = false;
    this.coinbaseProducts = new Map();
  }

  configure(config = {}) {
    this.config = { ...this.config, ...config };
  }

  start() {
    if (this.running || typeof WebSocket === "undefined") return;
    this.running = true;
    this.connectBinance();
    this.connectCoinbase();
    this.connectBybit();
    this.connectKraken();
  }

  stop() {
    this.running = false;
    this.reconnectTimers.forEach((timer) => clearTimeout(timer));
    this.pingTimers.forEach((timer) => clearInterval(timer));
    this.reconnectTimers.clear();
    this.pingTimers.clear();
    this.sockets.forEach((socket) => {
      try {
        socket.onclose = null;
        socket.close();
      } catch {}
    });
    this.sockets.clear();
  }

  scheduleReconnect(source, connect) {
    if (!this.running || this.reconnectTimers.has(source)) return;
    const attempt = (this.reconnectAttempts.get(source) || 0) + 1;
    this.reconnectAttempts.set(source, attempt);
    const delay = Math.min(30000, 1000 * (2 ** Math.min(5, attempt - 1)));
    const timer = setTimeout(() => {
      this.reconnectTimers.delete(source);
      connect.call(this);
    }, delay);
    this.reconnectTimers.set(source, timer);
  }

  openSocket(source, url, onOpen, onMessage, connect) {
    if (!this.running) return;
    try {
      const socket = new WebSocket(url);
      this.sockets.set(source, socket);
      socket.onopen = () => {
        this.reconnectAttempts.set(source, 0);
        onOpen?.(socket);
      };
      socket.onmessage = (event) => {
        try {
          onMessage(JSON.parse(event.data), Date.now(), socket);
        } catch (error) {
          console.warn(`[ExternalSignal:${source}] malformed message`, error?.message || error);
        }
      };
      socket.onerror = () => {};
      socket.onclose = () => {
        this.sockets.delete(source);
        const ping = this.pingTimers.get(source);
        if (ping) clearInterval(ping);
        this.pingTimers.delete(source);
        this.scheduleReconnect(source, connect);
      };
    } catch (error) {
      console.warn(`[ExternalSignal:${source}] connection failed`, error?.message || error);
      this.scheduleReconnect(source, connect);
    }
  }

  updateFeed(source, bidValue, askValue, exchangeTimestamp, receivedAt = Date.now()) {
    const bid = finitePositive(bidValue);
    const ask = finitePositive(askValue);
    if (!(bid > 0) || !(ask >= bid)) return;
    const mid = (bid + ask) / 2;
    const previous = this.feeds.get(source);
    const cutoff = receivedAt - this.config.historyWindowMs;
    const history = (previous?.history || []).filter((sample) => sample.t >= cutoff);
    const last = history[history.length - 1];
    if (!last || receivedAt - last.t >= 250 || Math.abs(last.mid - mid) > 0) {
      history.push({ t: receivedAt, mid });
    }
    this.feeds.set(source, {
      source,
      bid,
      ask,
      mid,
      exchangeTimestamp: safeTimestamp(exchangeTimestamp, receivedAt),
      receivedAt,
      history,
    });
  }

  connectBinance() {
    this.openSocket(
      "binance",
      "wss://stream.binance.com:9443/ws/ethusdc@bookTicker",
      null,
      (message, receivedAt) => this.updateFeed("binance", message.b, message.a, message.E, receivedAt),
      this.connectBinance
    );
  }

  connectCoinbase() {
    this.openSocket(
      "coinbase",
      "wss://advanced-trade-ws.coinbase.com",
      (socket) => {
        socket.send(JSON.stringify({
          type: "subscribe",
          product_ids: ["ETH-USD", "USDC-USD"],
          channel: "ticker",
        }));
        socket.send(JSON.stringify({ type: "subscribe", channel: "heartbeats" }));
      },
      (message, receivedAt) => {
        if (message.channel !== "ticker" || !Array.isArray(message.events)) return;
        message.events.forEach((event) => {
          (event.tickers || []).forEach((ticker) => {
            const productId = ticker.product_id;
            const bid = finitePositive(ticker.best_bid || ticker.price);
            const ask = finitePositive(ticker.best_ask || ticker.price);
            if (productId && bid && ask) {
              this.coinbaseProducts.set(productId, {
                bid,
                ask,
                timestamp: message.timestamp || receivedAt,
                receivedAt,
              });
            }
          });
        });
        const eth = this.coinbaseProducts.get("ETH-USD");
        const usdc = this.coinbaseProducts.get("USDC-USD");
        if (!eth || !usdc || receivedAt - eth.receivedAt > this.config.staleAfterMs || receivedAt - usdc.receivedAt > this.config.staleAfterMs) return;
        this.updateFeed(
          "coinbase",
          eth.bid / usdc.ask,
          eth.ask / usdc.bid,
          eth.timestamp,
          receivedAt
        );
      },
      this.connectCoinbase
    );
  }

  connectBybit() {
    this.openSocket(
      "bybit",
      "wss://stream.bybit.com/v5/public/spot",
      (socket) => {
        socket.send(JSON.stringify({ op: "subscribe", args: ["orderbook.1.ETHUSDC"] }));
        const ping = setInterval(() => {
          if (socket.readyState === WebSocket.OPEN) socket.send(JSON.stringify({ op: "ping" }));
        }, 20000);
        this.pingTimers.set("bybit", ping);
      },
      (message, receivedAt) => {
        if (message.topic !== "orderbook.1.ETHUSDC" || !message.data) return;
        const bid = message.data.b?.[0]?.[0];
        const ask = message.data.a?.[0]?.[0];
        this.updateFeed("bybit", bid, ask, message.cts || message.ts, receivedAt);
      },
      this.connectBybit
    );
  }

  connectKraken() {
    this.openSocket(
      "kraken",
      "wss://ws.kraken.com/v2",
      (socket) => socket.send(JSON.stringify({
        method: "subscribe",
        params: { channel: "ticker", symbol: ["ETH/USDC"], event_trigger: "bbo" },
      })),
      (message, receivedAt) => {
        if (message.channel !== "ticker" || !Array.isArray(message.data)) return;
        const ticker = message.data[0];
        this.updateFeed("kraken", ticker?.bid, ticker?.ask, ticker?.timestamp, receivedAt);
      },
      this.connectKraken
    );
  }

  getSnapshot(now = Date.now()) {
    const fresh = Array.from(this.feeds.values()).filter((feed) =>
      Math.max(now - feed.receivedAt, now - feed.exchangeTimestamp) <= this.config.staleAfterMs
    );
    const accepted = robustFilter(fresh, (feed) => feed.mid, this.config.outlierFloorBps);
    const sources = accepted.map((feed) => {
      const targetTime = now - this.config.momentumWindowMs;
      const history = feed.history || [];
      let reference = history[0];
      history.forEach((sample) => {
        if (sample.t <= targetTime) reference = sample;
      });
      const momentumBps = reference?.mid > 0
        ? ((feed.mid / reference.mid) - 1) * 10000
        : 0;
      const transportAgeMs = Math.max(0, now - feed.receivedAt);
      const exchangeAgeMs = Math.max(0, now - feed.exchangeTimestamp);
      return {
        source: feed.source,
        bid: feed.bid,
        ask: feed.ask,
        mid: feed.mid,
        ageMs: Math.max(transportAgeMs, exchangeAgeMs),
        transportAgeMs,
        exchangeAgeMs,
        momentumBps,
      };
    });

    const sourceCount = sources.length;
    const fairBid = median(sources.map((source) => source.bid));
    const fairAsk = median(sources.map((source) => source.ask));
    const fairPrice = fairBid > 0 && fairAsk > 0 ? (fairBid + fairAsk) / 2 : 0;
    const dispersionBps = fairPrice > 0
      ? (medianAbsoluteDeviation(sources.map((source) => source.mid), fairPrice) / fairPrice) * 10000
      : Infinity;
    const momentumBps = median(sources.map((source) => source.momentumBps));
    const momentumMad = medianAbsoluteDeviation(sources.map((source) => source.momentumBps), momentumBps);
    const nonFlatMomentum = sources.filter((source) => Math.abs(source.momentumBps) >= 1);
    const agreedMomentum = nonFlatMomentum.filter(
      (source) => Math.sign(source.momentumBps) === Math.sign(momentumBps)
    ).length;
    const agreement = nonFlatMomentum.length ? agreedMomentum / nonFlatMomentum.length : 1;
    const feedFreshnessMs = sources.length ? Math.max(...sources.map((source) => source.ageMs)) : Infinity;
    const freshnessScore = clamp(1 - feedFreshnessMs / this.config.staleAfterMs, 0, 1);
    const dispersionScore = clamp(1 - dispersionBps / this.config.maxDispersionBps, 0, 1);
    const momentumConsistency = clamp(1 - momentumMad / this.config.momentumScaleBps, 0, 1);
    const completeness = clamp(sourceCount / 4, 0, 1);
    const signalQuality = clamp(
      (freshnessScore * 0.3 + agreement * 0.3 + dispersionScore * 0.25 + momentumConsistency * 0.15) * completeness,
      0,
      1
    );
    const ready = sourceCount >= this.config.minSources && fairPrice > 0;

    return {
      status: !ready ? "warming" : signalQuality >= this.config.minSignalQuality ? "healthy" : "degraded",
      fairWethUsdc: fairPrice,
      fairBid,
      fairAsk,
      signalQuality,
      agreement,
      dispersionBps,
      momentumBps,
      momentumConsistency,
      feedFreshnessMs,
      sourceCount,
      sources,
      rejectedSources: fresh.filter((feed) => !accepted.includes(feed)).map((feed) => feed.source),
      calculatedAt: now,
    };
  }
}

export const createExternalMarketSignalService = (config = {}) =>
  new ExternalMarketSignalService(config);

export const buildExternalSignalAdvice = ({
  snapshot,
  side,
  poolPrice,
  dexExecutablePrice,
  baseTradeUsd,
  currentAdaptiveThresholdBps = 0,
  gasUsd,
  config = {},
}) => {
  const cfg = { ...DEFAULT_CONFIG, ...config };
  const normalizedSide = String(side || "").toUpperCase();
  const fairExecutable = normalizedSide === "BUY" ? snapshot?.fairBid : snapshot?.fairAsk;
  const fairPrice = finitePositive(snapshot?.fairWethUsdc);
  const valid =
    snapshot?.status === "healthy" &&
    finitePositive(poolPrice) &&
    finitePositive(dexExecutablePrice) &&
    fairPrice &&
    finitePositive(fairExecutable) &&
    finitePositive(baseTradeUsd);

  if (!valid) {
    return {
      status: snapshot?.status || "unavailable",
      reason: "External data or executable quote is not healthy; using neutral sizing.",
      entryEdgeBps: 0,
      rawDirectionalLeadBps: 0,
      directionalLeadBps: 0,
      neutralBandBps: 0,
      directionalExecutableEdgeBps: 0,
      netExecutableOpportunityBps: 0,
      opportunityMagnitudeBps: 0,
      opportunityDirection: "neutral",
      directionalMomentumBps: 0,
      signalConfirmed: false,
      executionAcceptable: false,
      supportScore: 0,
      sizeMultiplier: 1,
      recommendedTradeUsd: Number(baseTradeUsd) || 0,
      recommendedThresholdAdjustmentBps: 0,
      thresholdEligible: false,
      thresholdDirection: "neutral",
      costsBps: 0,
    };
  }

  const entryEdgeBps = normalizedSide === "BUY"
    ? ((fairExecutable / dexExecutablePrice) - 1) * 10000
    : ((dexExecutablePrice / fairExecutable) - 1) * 10000;
  const effectiveGasUsd = Number.isFinite(Number(gasUsd)) && Number(gasUsd) >= 0
    ? Number(gasUsd)
    : cfg.fallbackGasUsd;
  // Entry quote already includes entry fee/impact; reserve gas for entry and eventual exit.
  const gasBps = ((effectiveGasUsd * 2) / baseTradeUsd) * 10000;
  const costsBps = cfg.estimatedExitCostBps + cfg.safetyMarginBps + gasBps;
  // Execution economics are kept separate from direction. Costs can erase a
  // standalone opportunity, but they are not evidence that price will move
  // against the bot's existing grid decision.
  const directionalExecutableEdgeBps = entryEdgeBps - costsBps;
  const netExecutableOpportunityBps = Math.max(0, directionalExecutableEdgeBps);
  const opportunityMagnitudeBps = netExecutableOpportunityBps;
  const rawDirectionalLeadBps = normalizedSide === "BUY"
    ? ((fairPrice / poolPrice) - 1) * 10000
    : ((poolPrice / fairPrice) - 1) * 10000;
  const dispersionBps = Number.isFinite(Number(snapshot?.dispersionBps))
    ? Math.max(0, Number(snapshot.dispersionBps))
    : cfg.maxDispersionBps;
  const neutralBandBps = Math.max(
    cfg.minDirectionalLeadBps,
    dispersionBps * cfg.dispersionNoiseMultiplier
  );
  const directionalLeadBps = Math.abs(rawDirectionalLeadBps) > neutralBandBps
    ? Math.sign(rawDirectionalLeadBps) * (Math.abs(rawDirectionalLeadBps) - neutralBandBps)
    : 0;
  const directionalMomentumBps = normalizedSide === "BUY"
    ? snapshot.momentumBps
    : -snapshot.momentumBps;
  const agreement = clamp(Number(snapshot?.agreement) || 0, 0, 1);
  const momentumConsistency = clamp(Number(snapshot?.momentumConsistency) || 0, 0, 1);
  const qualityThreshold = Math.max(cfg.minSignalQuality, cfg.minDirectionalQuality);
  const qualityConfirmed = snapshot.signalQuality >= qualityThreshold;
  const agreementConfirmed = agreement >= cfg.minDirectionalAgreement;
  const consistencyConfirmed = momentumConsistency >= cfg.minMomentumConsistency;
  const momentumConfirmed = Math.abs(directionalMomentumBps) >= cfg.minDirectionalMomentumBps;
  const directionsAgree =
    directionalLeadBps !== 0 &&
    momentumConfirmed &&
    Math.sign(directionalLeadBps) === Math.sign(directionalMomentumBps);
  const signalConfirmed =
    qualityConfirmed &&
    agreementConfirmed &&
    consistencyConfirmed &&
    directionsAgree;
  const executionAcceptable = entryEdgeBps >= -cfg.executionToleranceBps;
  const opportunityScore = Math.tanh(netExecutableOpportunityBps / cfg.opportunityScaleBps);
  const leadScore = Math.tanh(directionalLeadBps / cfg.directionalLeadScaleBps);
  const momentumScore = Math.tanh(directionalMomentumBps / cfg.momentumScaleBps);
  let supportScore = 0;
  if (signalConfirmed && directionalLeadBps < 0) {
    supportScore = leadScore * 0.65 + momentumScore * 0.35;
  } else if (signalConfirmed && directionalLeadBps > 0 && executionAcceptable) {
    supportScore = leadScore * 0.55 + momentumScore * 0.3 + opportunityScore * 0.15;
  }
  const positiveRange = cfg.maxSizeMultiplier - 1;
  const negativeRange = 1 - cfg.minSizeMultiplier;
  const multiplierRange = supportScore >= 0 ? positiveRange : negativeRange;
  let sizeMultiplier = clamp(
    1 + supportScore * snapshot.signalQuality * multiplierRange,
    cfg.minSizeMultiplier,
    cfg.maxSizeMultiplier
  );
  if (sizeMultiplier > 1 && netExecutableOpportunityBps <= 0) {
    sizeMultiplier = Math.min(
      sizeMultiplier,
      clamp(cfg.maxUpsizeWithoutNetOpportunity, 1, cfg.maxSizeMultiplier)
    );
  }
  const opportunityDirection = sizeMultiplier > 1
    ? "supportive"
    : sizeMultiplier < 1
      ? "opposing"
      : "neutral";
  const thresholdLimit = Math.max(
    0,
    Number(currentAdaptiveThresholdBps) * clamp(cfg.maxThresholdAdjustmentPct, 0, 0.1)
  );
  const thresholdEligible = signalConfirmed && (directionalLeadBps < 0 || executionAcceptable);
  const recommendedThresholdAdjustmentBps = thresholdEligible
    ? -Math.sign(directionalLeadBps) * thresholdLimit
    : 0;
  const thresholdDirection = recommendedThresholdAdjustmentBps < 0
    ? "tighten"
    : recommendedThresholdAdjustmentBps > 0
      ? "widen"
      : "neutral";
  let reason;
  if (directionalLeadBps === 0) {
    reason = `CEX lead ${rawDirectionalLeadBps.toFixed(1)} bps is inside the ${neutralBandBps.toFixed(1)} bps neutral band`;
  } else if (!signalConfirmed) {
    reason = `Directional lead is not confirmed (quality ${(snapshot.signalQuality * 100).toFixed(0)}%, agreement ${(agreement * 100).toFixed(0)}%, momentum ${directionalMomentumBps.toFixed(1)} bps)`;
  } else if (directionalLeadBps > 0 && !executionAcceptable) {
    reason = `CEX direction supports the trade, but entry edge ${entryEdgeBps.toFixed(1)} bps is outside the ${cfg.executionToleranceBps.toFixed(1)} bps execution tolerance`;
  } else {
    reason = `${opportunityDirection} CEX lead ${rawDirectionalLeadBps.toFixed(1)} bps with ${(snapshot.signalQuality * 100).toFixed(0)}% signal quality`;
  }

  return {
    status: "healthy",
    reason,
    entryEdgeBps,
    rawDirectionalLeadBps,
    directionalLeadBps,
    neutralBandBps,
    directionalExecutableEdgeBps,
    netExecutableOpportunityBps,
    opportunityMagnitudeBps,
    opportunityDirection,
    directionalMomentumBps,
    signalConfirmed,
    executionAcceptable,
    supportScore,
    sizeMultiplier,
    recommendedTradeUsd: baseTradeUsd * sizeMultiplier,
    recommendedThresholdAdjustmentBps,
    thresholdEligible,
    thresholdDirection,
    gasBps,
    costsBps,
  };
};

export const applyExternalThresholdOverlay = ({
  side,
  currentPrice,
  currentTargetPrice,
  currentThresholdBps,
  economicFloorPrice = 0,
  advice,
  enabled,
  shadowMode,
  persistent,
  bypassReason = "",
  maxAdjustmentPct = DEFAULT_CONFIG.maxThresholdAdjustmentPct,
}) => {
  const normalizedSide = String(side || "").toUpperCase();
  const target = finitePositive(currentTargetPrice) || 0;
  const price = finitePositive(currentPrice) || 0;
  const threshold = Math.max(0, Number(currentThresholdBps) || 0);
  const adjustmentLimit = threshold * clamp(maxAdjustmentPct, 0, 0.1);
  const requestedAdjustmentBps = clamp(
    Number(advice?.recommendedThresholdAdjustmentBps) || 0,
    -adjustmentLimit,
    adjustmentLimit
  );
  const eligible =
    advice?.status === "healthy" &&
    advice?.signalConfirmed === true &&
    advice?.thresholdEligible === true &&
    requestedAdjustmentBps !== 0 &&
    !bypassReason;
  const canApply = enabled && !shadowMode && persistent && eligible && target > 0;
  const appliedAdjustmentBps = canApply ? requestedAdjustmentBps : 0;
  const adjustedThresholdBps = Math.max(0, threshold + appliedAdjustmentBps);

  let recommendedTargetPrice = target;
  let adjustedTargetPrice = target;
  if (target > 0 && normalizedSide === "BUY") {
    recommendedTargetPrice = target * (1 - requestedAdjustmentBps / 10000);
    adjustedTargetPrice = target * (1 - appliedAdjustmentBps / 10000);
  } else if (target > 0 && normalizedSide === "SELL") {
    recommendedTargetPrice = target * (1 + requestedAdjustmentBps / 10000);
    adjustedTargetPrice = target * (1 + appliedAdjustmentBps / 10000);
    if (economicFloorPrice > 0) {
      recommendedTargetPrice = Math.max(recommendedTargetPrice, economicFloorPrice);
      adjustedTargetPrice = Math.max(adjustedTargetPrice, economicFloorPrice);
    }
  }

  const originalWouldTrigger = normalizedSide === "BUY"
    ? price > 0 && target > 0 && price < target
    : normalizedSide === "SELL"
      ? price > 0 && target > 0 && price > target
      : false;
  const recommendedWouldTrigger = normalizedSide === "BUY"
    ? price > 0 && recommendedTargetPrice > 0 && price < recommendedTargetPrice
    : normalizedSide === "SELL"
      ? price > 0 && recommendedTargetPrice > 0 && price > recommendedTargetPrice
      : false;
  const adjustedWouldTrigger = normalizedSide === "BUY"
    ? price > 0 && adjustedTargetPrice > 0 && price < adjustedTargetPrice
    : normalizedSide === "SELL"
      ? price > 0 && adjustedTargetPrice > 0 && price > adjustedTargetPrice
      : false;

  return {
    eligible,
    persistent: persistent === true,
    requestedAdjustmentBps,
    appliedAdjustmentBps,
    currentThresholdBps: threshold,
    recommendedThresholdBps: Math.max(0, threshold + requestedAdjustmentBps),
    adjustedThresholdBps,
    originalTargetPrice: target,
    recommendedTargetPrice,
    adjustedTargetPrice,
    originalWouldTrigger,
    recommendedWouldTrigger,
    adjustedWouldTrigger,
    changedAnything: Math.abs(appliedAdjustmentBps) >= 0.001,
    blockedReason: !eligible
      ? (bypassReason || "threshold signal is not eligible")
      : !enabled
        ? "Phase 2 is disabled"
        : shadowMode
          ? "Phase 2 is in shadow mode"
          : !persistent
            ? "waiting for persistent confirmation"
            : "",
  };
};

export const applyExternalSizeOverlay = ({
  baseTradeUsd,
  advice,
  enabled,
  shadowMode,
  minTradeUsd,
  maxTradeUsd,
  availableUsd,
}) => {
  const base = Math.max(0, Number(baseTradeUsd) || 0);
  const hardMin = Math.max(0, Number(minTradeUsd) || 0);
  const hardMax = Math.max(hardMin, Number(maxTradeUsd) || base);
  const available = Math.max(0, Number(availableUsd) || 0);
  const canApply = enabled && !shadowMode && advice?.status === "healthy";
  if (!canApply) {
    return {
      finalTradeUsd: base,
      appliedMultiplier: 1,
      changedAnything: false,
      constrainedByLimits: false,
    };
  }
  const requested = canApply ? base * advice.sizeMultiplier : base;
  const bounded = Math.min(Math.max(requested, hardMin), hardMax, available);
  const finalTradeUsd = base > 0 ? bounded : 0;
  return {
    finalTradeUsd,
    appliedMultiplier: base > 0 ? finalTradeUsd / base : 1,
    changedAnything: canApply && Math.abs(finalTradeUsd - base) >= 0.01,
    constrainedByLimits: canApply && Math.abs(requested - finalTradeUsd) >= 0.01,
  };
};
